import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";
import { signIn as sendSignInRequest } from "../api/auth";
import { requestERPLYData } from "../utils/requestERPLY";
import notify from "devextreme/ui/notify";
import { partnerKey } from "../utils";
import Server from "../api/Server";

function AuthProvider(props) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [wareHouses, setWarehouses] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [userRights, setConfig] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [activeLangs, setActiveLang] = useState([]);
  const [userConfig, setUserConfig] = useState({});
  const [userRightAuth, setUserRightAuth] = useState(null);
  const [loginUrl, setLink] = useState("https://login.erply.com");

  useEffect(() => {
    let urlSearchParams = new URLSearchParams(window.location.search);
    let clientCode = urlSearchParams.get("clientCode");
    let sessionKey = urlSearchParams.get("sessionKey");
    let lang = urlSearchParams.get("lang");
    let origin = window.location.origin.trim();
    let target = `${origin}/?clientCode={CLIENTCODE}`;
    target = encodeURIComponent(target);

    const loginLink = `https://login.erply.com/?target=${target}&clientCode=${clientCode}`;

    setLink(loginLink);

    if (clientCode) {
      requestERPLYData({
        clientCode,
        request: "getServiceEndpoints",
        partnerKey,
      }).then((res) => {
        const baseData = res;
        if (res.data.status.responseStatus === "ok") {
          const session = sessionKey ? sessionKey : null;
          getAccounts(baseData.data.records[0].cafa.url, {
            sessionKey: sessionKey ? sessionKey : "",
            clientCode,
          });
          getWarehouses(session, clientCode);
          getAllLanguages(session, clientCode);
          getActiveLanguages(session, clientCode);
          getConfParameters(session, clientCode);
          getAllUsers(session, clientCode);
          getGeneralConfig(session, clientCode);
          getRegisters(baseData.data.records[0]["account-admin"].url, {
            sessionKey: sessionKey ? sessionKey : "",
            clientCode,
          });
          getUserRightsAuth(baseData.data.records[0]["account-admin"].url, {
            sessionKey: sessionKey ? sessionKey : "",
            clientCode,
          });

          const requestData = {
            request: "getSessionKeyUser",
            clientCode,
            partnerKey,
          };

          if (sessionKey) {
            requestData.sessionKey = sessionKey;
          }

          requestERPLYData(requestData).then((res) => {
            const result = res;
            if (result.data.status.responseStatus === "ok") {
              setUser({
                baseUrl: baseData.data.records[0].cafa.url,
                accountAdminUrl: baseData.data.records[0]["account-admin"].url
                  ? baseData.data.records[0]["account-admin"].url
                  : "",
                sessionKey: sessionKey ? sessionKey : "",
                clientCode,
                lang: lang ? lang : "eng",
                user: result.data.records[0].userName,
                isIframe: false,
              });
              setLoading(false);
            } else {
              setUser(null);
              setLoading(false);
            }
          });
        } else {
          setLoading(false);
          notify(`Error performing operation`, "error", 3000);
        }
      });
    } else {
      window.parent.postMessage("apiSessionKey", "*");
      window.addEventListener(
        "message",
        (message) => {
          if (
            message.origin.includes("erply.com") &&
            message.data.sender === "erply" &&
            message.data.apiSessionKey
          ) {
            requestERPLYData({
              clientCode: message.data.clientCode,
              request: "getServiceEndpoints",
              partnerKey,
            }).then((res) => {
              const baseData = res;
              if (res.data.status.responseStatus === "ok") {
                getAccounts(baseData.data.records[0].cafa.url, {
                  sessionKey: message.data.apiSessionKey,
                  clientCode: message.data.clientCode,
                });
                getWarehouses(
                  message.data.apiSessionKey,
                  message.data.clientCode
                );
                getAllLanguages(
                  message.data.apiSessionKey,
                  message.data.clientCode
                );

                getActiveLanguages(
                  message.data.apiSessionKey,
                  message.data.clientCode
                );

                getConfParameters(
                  message.data.apiSessionKey,
                  message.data.clientCode
                );

                getGeneralConfig(
                  message.data.apiSessionKey,
                  message.data.clientCode
                );
                getAllUsers(
                  message.data.apiSessionKey,
                  message.data.clientCode
                );
                getRegisters(baseData.data.records[0]["account-admin"].url, {
                  sessionKey: message.data.apiSessionKey,
                  clientCode: message.data.clientCode,
                });
                getUserRightsAuth(
                  baseData.data.records[0]["account-admin"].url,
                  {
                    sessionKey: message.data.apiSessionKey,
                    clientCode: message.data.clientCode,
                  }
                );
                requestERPLYData({
                  request: "getSessionKeyUser",
                  sessionKey: message.data.apiSessionKey,
                  clientCode: message.data.clientCode,
                  partnerKey,
                }).then((res) => {
                  const result = res;
                  if (result.data.status.responseStatus === "ok") {
                    setUser({
                      baseUrl: baseData.data.records[0].cafa.url,
                      accountAdminUrl: baseData.data.records[0]["account-admin"]
                        .url
                        ? baseData.data.records[0]["account-admin"].url
                        : "",
                      sessionKey: message.data.apiSessionKey,
                      clientCode: message.data.clientCode,
                      lang: message.data.page_lang,
                      user: result.data.records[0].userName,
                      isIframe: true,
                    });
                    setLoading(false);
                  } else {
                    setUser(null);
                    setLoading(false);
                  }
                });
              } else {
                setLoading(false);
                notify(`Error performing operation`, "error", 3000);
              }
            });
          }
        },
        false
      );
    }
  }, []);

  const signIn = useCallback(async (email, password) => {
    const result = await sendSignInRequest(email, password);
    if (result.isOk) {
      setUser(result.data);
    }

    return result;
  }, []);

  const getAccounts = async (baseURL, keys) => {
    try {
      const {
        data: { applications },
      } = await Server.getAccounts(baseURL, keys);

      if (applications) {
        setAccounts(
          applications.map((app, index) => ({ value: app, id: index }))
        );
      }
    } catch (error) {
      const {
        response: { data },
      } = error;

      if (data) {
        notify(`${data.message}`, "error", 3000);
      } else {
        notify(`Error performing operation`, "error", 3000);
      }
    }
  };

  const getWarehouses = async (sessionKey, clientCode) => {
    const request = {
      request: "getWarehouses",
      clientCode,
      partnerKey,
      sendContentType: 1,
    };

    if (sessionKey) {
      request.sessionKey = sessionKey;
    }

    try {
      const { data } = await requestERPLYData(request);
      if (data.status.responseStatus === "ok") {
        setWarehouses(data.records);
      }
    } catch (error) {}
  };

  const getRegisters = async (baseURL, keys) => {
    try {
      const { data } = await Server.getRegisters(baseURL, keys);
      setRegisters(data.data);
    } catch (error) {}
  };

  const getAllUsers = async (sessionKey, clientCode) => {
    const request = {
      request: "getUserRights",
      clientCode,
      partnerKey,
      sendContentType: 1,
    };

    if (sessionKey) {
      request.sessionKey = sessionKey;
    }
    try {
      const { data } = await requestERPLYData(request);
      if (data.status.responseStatus === "ok") {
        setAllUsers(data.records);
      }
    } catch (error) {}
  };

  const getGeneralConfig = async (sessionKey, clientCode) => {
    const request = {
      request: "getUserRights",
      getCurrentUser: 1,
      clientCode,
      partnerKey,
      sendContentType: 1,
    };
    if (sessionKey) {
      request.sessionKey = sessionKey;
    }

    try {
      const { data } = await requestERPLYData(request);
      if (data.status.responseStatus === "ok") {
        setConfig(data.records);
      }
    } catch (error) {}
  };

  const getUserRightsAuth = async (baseURL, keys) => {
    try {
      const { data } = await Server.getUserRights(baseURL, keys);
      setUserRightAuth(data.data);
    } catch (error) {}
  };

  const getAllLanguages = async (sessionKey, clientCode) => {
    const request = {
      request: "getAllLanguages",
      clientCode,
      partnerKey,
      sendContentType: 1,
    };

    if (sessionKey) {
      request.sessionKey = sessionKey;
    }

    try {
      const { data } = await requestERPLYData(request);
      if (data.status.responseStatus === "ok") {
        setLanguages(data.records);
      }
    } catch (error) {}
  };

  const getActiveLanguages = async (sessionKey, clientCode) => {
    const request = {
      request: "getActiveLanguages",
      clientCode,
      partnerKey,
      sendContentType: 1,
    };

    if (sessionKey) {
      request.sessionKey = sessionKey;
    }

    try {
      const { data } = await requestERPLYData(request);
      if (data.status.responseStatus === "ok") {
        setActiveLang(data.records);
      }
    } catch (error) {}
  };

  const getConfParameters = async (sessionKey, clientCode) => {
    const request = {
      request: "getConfParameters",
      clientCode,
      partnerKey,
      sendContentType: 1,
    };

    if (sessionKey) {
      request.sessionKey = sessionKey;
    }

    try {
      const { data } = await requestERPLYData(request);
      if (data.status.responseStatus === "ok") {
        setUserConfig(data.records[0]);
      }
    } catch (error) {}
  };

  const signOut = useCallback(() => {
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signOut,
        loading,
        accounts,
        wareHouses,
        registers,
        allUsers,
        userRights,
        loginUrl,
        getAccounts,
        activeLangs,
        languages,
        userConfig,
        userRightAuth,
      }}
      {...props}
    />
  );
}

const AuthContext = createContext({});
const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
